import { AdminNotifySummary } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Box, DataTable, Loader, Text } from '@tocktix/pantry';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { formatNotifyDates, formatShortDate, formatTimeRange } from 'tools/users/dateFormatters';
import { userClient } from 'tools/bff/client';
import StatusTag, { StatusDisplay } from 'tools/users/StatusTag';

type NotifyHistorySectionProps = {
  tockPatronId?: bigint;
  enabled: boolean;
  // Accepted now for symmetry with ReservationHistorySection / forward-compat; unused in v1.
  resyUserId?: bigint;
};

const notifyStatusDisplay: Record<string, StatusDisplay> = {
  ACTIVE: { label: 'Active', color: 'primary' },
  NOTIFIED: { label: 'Notified', color: 'warning' },
  FULFILLED: { label: 'Fulfilled', color: 'success' },
  CANCELED: { label: 'Canceled', color: 'secondary' },
};

const NotifyHistorySection: FunctionComponent<NotifyHistorySectionProps> = ({
  tockPatronId,
  enabled,
}) => {
  const [notifies, setNotifies] = useState<AdminNotifySummary[]>([]);
  const [notifiesLoading, setNotifiesLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !tockPatronId || hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    let isMounted = true;
    const loadNotifies = async () => {
      setNotifiesLoading(true);
      try {
        const res = await userClient.getUserNotifyHistory({ tockPatronId, limit: 10 });
        if (isMounted) {
          setNotifies(res.notifies);
        }
      } catch {
        // Notify fetch failure is non-fatal; leave the list empty.
      } finally {
        if (isMounted) {
          setNotifiesLoading(false);
        }
      }
    };

    loadNotifies();
    return () => {
      isMounted = false;
    };
  }, [enabled, tockPatronId]);

  return (
    <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {notifiesLoading ? (
        <Box padding={300} display="flex" justifyContent="center">
          <Loader aria-label="notifies-loading" size="md" />
        </Box>
      ) : (
        <Box padding={300} display="flex" flexDirection="column" gap={200}>
          {notifies.length === 0 ? (
            <Text variant="body" color="fgSubtle">
              No notify history available.
            </Text>
          ) : (
            <DataTable id="notify-history-table">
              <DataTable.HeaderRow>
                <DataTable.Header id="venue" title="Venue" />
                <DataTable.Header id="notifyDate" title="Notify Date" />
                <DataTable.Header id="partySize" title="Party Size" />
                <DataTable.Header id="preferredTime" title="Preferred Time" />
                <DataTable.Header id="dateCreated" title="Date Created" />
                <DataTable.Header id="status" title="Status" />
              </DataTable.HeaderRow>
              <DataTable.Body>
                {notifies.map((summary, index) => {
                  return (
                    <DataTable.Row key={index} id={index} index={index}>
                      <DataTable.Cell columnId="venue">
                        {summary.venueName ?? '\u2014'}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="notifyDate">
                        {formatNotifyDates(summary.notifyDates)}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="partySize">
                        {summary.partySize ?? '\u2014'}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="preferredTime">
                        {formatTimeRange(summary.preferredStartTime, summary.preferredEndTime)}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="dateCreated">
                        {summary.createdAt
                          ? formatShortDate(summary.createdAt.split('T')[0])
                          : '\u2014'}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="status">
                        <StatusTag value={summary.status} displayMap={notifyStatusDisplay} />
                      </DataTable.Cell>
                    </DataTable.Row>
                  );
                })}
              </DataTable.Body>
            </DataTable>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NotifyHistorySection;
